import {
  useState,
  ChangeEventHandler,
  FocusEventHandler,
  FormEventHandler,
  WheelEventHandler,
  useEffect,
} from 'react';

import { Input } from '../Input';
import Typography from '../Typography';

import { TimeParts, pad } from './helpers';

function TimeNumberInput({
  timeParts,
  setTimeParts,
  name,
  min,
  max,
  placeholder,
}: {
  timeParts: TimeParts;
  setTimeParts: (timeParts: TimeParts) => void;
  name: keyof Omit<TimeParts, 'isPm'>;
  min: number;
  max: number;
  placeholder: string;
}) {
  const onBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    e.currentTarget.value = pad(e.currentTarget.value);
    setTimeParts({ ...timeParts, [name]: e.target.value });
  };

  const onWheel: WheelEventHandler<HTMLInputElement> = (e) => {
    e.currentTarget.blur();
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    //console.log(e.currentTarget.validationMessage);
    setTimeParts({ ...timeParts, [name]: e.target.value });
  };

  const onInput: FormEventHandler<HTMLInputElement> = (e) => {
    if (e.currentTarget.value.length > 2) {
      e.currentTarget.value = e.currentTarget.value.slice(0, 2);
    }
  };

  const value = timeParts?.[name] ?? '';

  return (
    <Input
      {...{
        style: inputStyles,
        placeholder,
        value,
        onInput,
        onChange,
        onBlur,
        onWheel,
        type: 'number',
        maxLength: 2,
        tabIndex: -1,
        min,
        max,
      }}
    />
  );
}

const inputStyles: React.CSSProperties = {
  border: 'none',
  width: '40px',
  height: '36px',
  background: '#F6F8FE',
  padding: '7px',
  fontSize: '14px',
  textAlign: 'center',
};

function AMPMToggle({ onChange, value }: { onChange: (value: boolean) => void; value: boolean }) {
  return (
    <div className="relative inline-flex items-center bg-[#F6F8FE] rounded-[8px] p-1 w-24 h-9">
      <div
        className={`absolute w-11 h-8 bg-white rounded-[7px] shadow transition-transform duration-300 ease-in-out ${
          !value ? 'translate-x-0' : 'translate-x-full'
        }`}
      ></div>
      <div className="z-10 w-1/2 text-center cursor-pointer" onClick={() => onChange(false)}>
        <Typography fontWeight={400} variant="p3">
          AM
        </Typography>
      </div>
      <div className="z-10 w-1/2 text-center cursor-pointer" onClick={() => onChange(true)}>
        <Typography fontWeight={400} variant="p3">
          PM
        </Typography>
      </div>
    </div>
  );
}

export function TimeEditor({
  initialValue,
  onChange,
  error,
  setError,
}: {
  initialValue: TimeParts;
  onChange: (value?: TimeParts) => void;
  error?: string;
  setError: (err: string) => void;
}) {
  const [timeParts, setTimeParts] = useState<TimeParts>({
    hours: pad(initialValue.hours),
    minutes: pad(initialValue.minutes),
    isPm: initialValue.isPm,
  });

  // const [timeParts, setTimeParts] = useState<TimeParts>(value);

  // const [error, setError] = useState('');

  useEffect(() => {
    const h = parseInt(timeParts.hours);
    const m = parseInt(timeParts.minutes);
    // console.log({ h, m, timeParts });
    let error = '';
    if (isNaN(h) || h < 0 || h > 11) {
      error = 'Hours must be between 0 and 11';
    }
    if (isNaN(m) || m < 0 || m > 59) {
      error = 'Minute must be between 0 and 59';
    }
    setError(error);

    onChange(error ? undefined : timeParts);
  }, [onChange, timeParts, setError]);

  // const err = error || externalError;

  return (
    <div className="flex flex-col gap-[13px]">
      <Typography variant="p3" fontWeight={400}>
        Time
      </Typography>
      <div className="flex gap-2">
        <div className="flex items-center gap-1">
          <TimeNumberInput
            min={0}
            max={11}
            placeholder="HH"
            name="hours"
            timeParts={timeParts}
            setTimeParts={setTimeParts}
          />
          :
          <TimeNumberInput
            min={0}
            max={59}
            placeholder="mm"
            name="minutes"
            timeParts={timeParts}
            setTimeParts={setTimeParts}
          />
        </div>
        <AMPMToggle
          value={timeParts.isPm}
          onChange={(isPm) => setTimeParts((p) => ({ ...p, isPm }))}
        />
      </div>
      {error && (
        <Typography variant="p3" fontWeight={400} color="text-red">
          {error}
        </Typography>
      )}
    </div>
  );
}
